<template>
  <el-dialog
    :visible="true"
    :show-close="false"
    class="changelog-detail"
    @close="$emit('dialogClose', { success: false })"
  >
    <ContentHeader
      slot="title"
      :title="title"
    />
    <div class="changelog-detail-content">
      <!-- Information -->
      <div v-if="logInformation">
        <el-col
          :span="24"
          class="section-subtitle"
        >
          {{ $t('changeLog.detail.information') }}
        </el-col>
        <div
          v-for="(logKey, index) in Object.keys(logInformation)"
          :key="index"
        >
          <el-row
            v-if="logInformation[logKey]"
            :gutter="20"
          >
            <el-col
              :span="12"
              class="updated-col"
            >
              {{ checkTranslation(logKey, `filters.${logKey}`) }}
            </el-col>
            <el-col
              :span="12"
              class="updated-col"
            >
              {{ logInformation[logKey] }}
            </el-col>
          </el-row>
        </div>
      </div>

      <!-- Added fields -->
      <div
        v-if="Object.keys(addedData).length"
        :gutter="20"
      >
        <el-col
          :span="24"
          class="section-subtitle"
        >
          {{ $t('changeLog.detail.added') }}
        </el-col>
        <div
          v-for="(key, index) in Object.keys(addedData)"
          :key="index"
          :class="{ 'multiples-items-row': isMultiRow(key) }"
        >
          <el-row
            v-if="isMultiRow(key)"
            :gutter="20"
          >
            <div
              v-for="(field, index) in Object.keys(addedData[key])"
              :key="index"
            >
              <div v-if="addedData[key][field]">
                <el-col
                  :span="12"
                  class="updated-col"
                >
                  {{ checkTranslation(field, `filters.${field}`) }}
                </el-col>
                <el-col
                  :span="12"
                  class="updated-col"
                >
                  {{ addedData[key][field] }}
                </el-col>
              </div>
            </div>
          </el-row>
          <el-row v-else-if="addedData[key]">
            <el-col
              :span="12"
              class="updated-col"
            >
              {{ checkTranslation(key, `filters.${key}`) }}
            </el-col>
            <el-col
              :span="12"
              class="updated-col"
            >
              {{ addedData[key] }}
            </el-col>
          </el-row>
        </div>
      </div>

      <!-- Updated fields -->
      <div
        v-if="Object.keys(updatedData).length"
        :gutter="20"
      >
        <el-col
          class="section-subtitle"
          :span="24"
        >
          {{ $t('changeLog.detail.updated') }}
        </el-col>
        <el-row :gutter="20">
          <el-col
            class="col-title"
            :span="8"
          >
            {{ $t('changeLog.detail.field') }}
          </el-col>
          <el-col
            class="col-title"
            :span="8"
          >
            <span v-if="log.entityName === 'RoleConfig'">
              {{ $t('changeLog.detail.permissionsRemoved') }}
            </span>
            <span v-else>
              {{ $t('changeLog.detail.oldValue') }}
            </span>
          </el-col>
          <el-col
            class="col-title"
            :span="8"
          >
            <span v-if="log.entityName === 'RoleConfig'">
              {{ $t('changeLog.detail.permissionsAdded') }}
            </span>
            <span v-else>
              {{ $t('changeLog.detail.newValue') }}
            </span>
          </el-col>
        </el-row>
        <div
          v-for="(key, index) in Object.keys(updatedData)"
          :key="index"
          :class="{ 'multiples-items-row': isMultiRow(key) }"
        >
          <div v-if="isMultiRow(key)">
            <el-row
              :gutter="20"
              v-for="updatedKey in visibleNestedKeys(key)"
              :key="updatedKey"
            >
              <el-col
                class="updated-col"
                :span="8"
              >
                {{ checkTranslation(updatedKey, `filters.${updatedKey}`) }}
              </el-col>
              <el-col
                class="updated-col"
                :span="8"
              >
                <span>
                  {{ changeLog.oldValueJson[key][updatedKey] }}
                </span>
              </el-col>
              <el-col
                class="updated-col"
                :span="8"
              >
                <span>
                  {{ updatedData[key].newValue[updatedKey] }}
                </span>
              </el-col>
            </el-row>
          </div>
          <el-row
            v-else
            :gutter="20"
          >
            <el-col
              class="updated-col"
              :span="8"
            >
              {{ checkTranslation(key, `filters.${key}`) }}
            </el-col>
            <el-col
              class="updated-col"
              :span="8"
            >
              <span :class="{ multiline: key === 'permissions' }">
                {{ updatedData[key].oldValue }}
              </span>
            </el-col>
            <el-col
              class="updated-col"
              :span="8"
            >
              <span :class="{ multiline: key === 'permissions' }">
                {{ updatedData[key].newValue }}
              </span>
            </el-col>
          </el-row>
        </div>
      </div>

      <!-- Removed fields -->
      <div
        v-if="Object.keys(removedData).length"
        :gutter="20"
      >
        <el-col
          :span="24"
          class="section-subtitle"
        >
          {{ $t('changeLog.detail.removed') }}
        </el-col>
        <div
          v-for="(key, index) in Object.keys(removedData)"
          :key="index"
          :class="{ 'multiples-items-row': isMultiRow(key) }"
        >
          <el-row
            v-if="isMultiRow(key)"
            :gutter="20"
          >
            <div
              v-for="(field, index) in Object.keys(removedData[key])"
              :key="index"
            >
              <div v-if="removedData[key][field]">
                <el-col
                  :span="12"
                  class="updated-col"
                >
                  {{ checkTranslation(field, `filters.${field}`) }}
                </el-col>
                <el-col
                  :span="12"
                  class="updated-col"
                >
                  {{ removedData[key][field] }}
                </el-col>
              </div>
            </div>
          </el-row>
          <el-row v-else-if="removedData[key]">
            <el-col
              :span="12"
              class="updated-col"
            >
              {{ checkTranslation(key, `filters.${key}`) }}
            </el-col>
            <el-col
              :span="12"
              class="updated-col"
            >
              {{ removedData[key] }}
            </el-col>
          </el-row>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
  import moment from 'moment';
  import {
    LAB_SAMPLE_STATUSES_DESC,
    QUARANTINE_REASONS_DESC,
    SAMPLE_ANALYSIS_STATUSES_DESC,
  } from '@/config/constants';
  import ContentHeader from '@/components/ContentHeader.vue';

  export default {
    name: 'ChangeLogDetail',
    components: {
      ContentHeader,
    },
    props: {
      log: { type: Object, default: () => {} },
      changeLog: { type: Object, default: () => {} },
    },
    data() {
      return {
        LAB_SAMPLE_STATUSES_DESC,
        QUARANTINE_REASONS_DESC,
        SAMPLE_ANALYSIS_STATUSES_DESC,
      };
    },
    computed: {
      title() {
        return this.log.entityName
          ? this.checkTranslation(
              this.log.entityName,
              `enums.entities.${this.log.entityName}`
            )
          : '';
      },
      logInformation() {
        switch (this.log.entityName) {
          case 'Sample':
          case 'LabSample':
            if (this.log.labSampleCode) {
              return {
                labSampleCode: this.log.labSampleCode,
              };
            }
            return null;
          case 'SampleAnalysisOperationValue':
          case 'LabSampleAnalysisOperationValue':
            return {
              labSampleCode: this.log.labSampleCode,
              analysisCode: this.log.analysisCode,
              analysisDescription: this.log.analysisDescription,
              operation: this.log.operationName,
            };
          case 'OperationRegistry':
          case 'AnalysisSpecification':
            return {
              analysisCode: this.log.analysisCode,
              analysisDescription: this.log.analysisDescription,
              operation: this.changeLog.oldValueJson.operation ?? '',
            };
          case 'UserRoles':
            if (this.log.affectedUserName) {
              return {
                userName: this.log.affectedUserName,
              };
            }
            return null;
          case 'RoleConfig':
            if (this.log.roleName || this.log.roleId) {
              return {
                role: this.log.roleName ?? this.log.roleId,
              };
            }
            return null;
          case 'Quarantine':
            if (this.log.labSampleCode) {
              return {
                labSampleCode: this.log.labSampleCode,
              };
            }
            return null;
          case 'Thermostat':
            if (this.log.thermostatName) {
              return {
                thermostat: this.log.thermostatName,
              };
            }
            return null;
          case 'LabSampleAnalysisStatus':
            if (this.log.labSampleCode || this.log.analysisCode) {
              return {
                labSampleCode: this.log.labSampleCode,
                analysisCode: this.log.analysisCode,
              };
            }
            return null;
          case 'ReportSampleInfo':
          case 'ReportTemperatureLog':
          case 'SampleReportRevision':
            return {
              reportCode: this.log.reportCode || this.log.sampleReportId,
              labSampleCode: this.log.labSampleCode,
              revision: this.log.revision,
            };
          default:
            return null;
        }
      },
      addedData() {
        const changeJsonA = { ...this.changeLog.changeJson.a } || null;
        return this.prepareData(changeJsonA);
      },
      updatedData() {
        const changeJsonU = { ...this.changeLog.changeJson.u } || null;
        if (changeJsonU) {
          const keys = Object.keys(changeJsonU) || [];
          if (keys.filter((k) => Number(k)).length > 0) {
            keys.forEach((k) => {
              if (this.changeLog.oldValueJson[k]) {
                const oldValueKeys = Object.keys(
                  this.changeLog.oldValueJson[k]
                );
                oldValueKeys.forEach((opKey) => {
                  if (opKey === 'laboratory') {
                    this.changeLog.oldValueJson[k][opKey] =
                      this.checkTranslation(
                        this.changeLog.oldValueJson[k][opKey],
                        `constants.${this.changeLog.oldValueJson[k][opKey]}`
                      );
                  }
                });
              }
              if (changeJsonU[k].newValue) {
                const newValueKeys = Object.keys(changeJsonU[k].newValue);
                newValueKeys.forEach((opKey) => {
                  if (opKey === 'laboratory') {
                    changeJsonU[k].newValue[opKey] = this.checkTranslation(
                      changeJsonU[k].newValue[opKey],
                      `constants.${changeJsonU[k].newValue[opKey]}`
                    );
                  }
                });
              }
            });
            return changeJsonU;
          }
          keys.forEach((k) => {
            if (k === 'status') {
              if (changeJsonU[k].oldValue || changeJsonU[k].oldValue === 0) {
                const status =
                  LAB_SAMPLE_STATUSES_DESC[changeJsonU[k].oldValue];
                changeJsonU[k].oldValue = this.checkTranslation(
                  status,
                  `enums.sampleStatuses.${status}`
                );
              } else if (
                this.changeLog.oldValueJson[k] ||
                this.changeLog.oldValueJson[k] === 0
              ) {
                const status =
                  LAB_SAMPLE_STATUSES_DESC[this.changeLog.oldValueJson[k]];
                changeJsonU[k].oldValue = this.checkTranslation(
                  status,
                  `enums.sampleStatuses.${status}`
                );
              }
              if (changeJsonU[k].newValue || changeJsonU[k].newValue === 0) {
                const status =
                  LAB_SAMPLE_STATUSES_DESC[changeJsonU[k].newValue];
                changeJsonU[k].newValue = this.checkTranslation(
                  status,
                  `enums.sampleStatuses.${status}`
                );
              }
            } else if (k === 'analysisStatus') {
              if (changeJsonU[k].oldValue || changeJsonU[k].oldValue === 0) {
                const analysisStatus =
                  SAMPLE_ANALYSIS_STATUSES_DESC[changeJsonU[k].oldValue];
                changeJsonU[k].oldValue = this.checkTranslation(
                  analysisStatus,
                  `enums.sampleStatuses.${analysisStatus}`
                );
              } else if (
                this.changeLog.oldValueJson[k] ||
                this.changeLog.oldValueJson[k] === 0
              ) {
                const analysisStatus =
                  SAMPLE_ANALYSIS_STATUSES_DESC[this.changeLog.oldValueJson[k]];
                changeJsonU[k].oldValue = this.checkTranslation(
                  analysisStatus,
                  `enums.sampleStatuses.${analysisStatus}`
                );
              }
              if (changeJsonU[k].newValue || changeJsonU[k].newValue === 0) {
                const analysisStatus =
                  SAMPLE_ANALYSIS_STATUSES_DESC[changeJsonU[k].newValue];
                changeJsonU[k].newValue = this.checkTranslation(
                  analysisStatus,
                  `enums.sampleStatuses.${analysisStatus}`
                );
              }
            } else if (k === 'permissions') {
              if (changeJsonU[k].oldValue && changeJsonU[k].oldValue.length) {
                const permissionsFiltered = changeJsonU[k].oldValue.filter(
                  (p) => !changeJsonU[k].newValue.includes(p)
                );
                changeJsonU[k].oldValue =
                  this.parsePermissions(permissionsFiltered);
              } else if (
                this.changeLog.oldValueJson[k] &&
                this.changeLog.oldValueJson[k].length
              ) {
                const permissionsFiltered = this.changeLog.oldValueJson[
                  k
                ].filter((p) => !changeJsonU[k].newValue.includes(p));
                changeJsonU[k].oldValue =
                  this.parsePermissions(permissionsFiltered);
              }
              if (changeJsonU[k].newValue && changeJsonU[k].newValue.length) {
                const permissionsFiltered = changeJsonU[k].newValue.filter(
                  (p) => !this.changeLog.oldValueJson[k].includes(p)
                );
                changeJsonU[k].newValue =
                  this.parsePermissions(permissionsFiltered);
              }
            } else if (k === 'laboratory') {
              if (changeJsonU[k].oldValue) {
                changeJsonU[k].oldValue = this.checkTranslation(
                  changeJsonU[k].oldValue,
                  `constants.${changeJsonU[k].oldValue}`
                );
              } else if (this.changeLog.oldValueJson[k]) {
                changeJsonU[k].oldValue = this.checkTranslation(
                  this.changeLog.oldValueJson[k],
                  `constants.${this.changeLog.oldValueJson[k]}`
                );
              }
              if (changeJsonU[k].newValue) {
                changeJsonU[k].newValue = this.checkTranslation(
                  changeJsonU[k].newValue,
                  `constants.${changeJsonU[k].newValue}`
                );
              }
            } else if (k === 'type' || k === 'specificationType') {
              if (changeJsonU[k].oldValue) {
                changeJsonU[k].oldValue = this.parseSpecificationFormat(
                  changeJsonU[k].oldValue
                );
              } else if (this.changeLog.oldValueJson[k]) {
                this.changeLog.oldValueJson[k] = this.parseSpecificationFormat(
                  this.changeLog.oldValueJson[k]
                );
              }
              if (changeJsonU[k].newValue) {
                changeJsonU[k].newValue = this.parseSpecificationFormat(
                  changeJsonU[k].newValue
                );
              }
            } else if (k === 'symbolType') {
              if (changeJsonU[k].oldValue) {
                changeJsonU[k].oldValue = this.checkTranslation(
                  changeJsonU[k].oldValue,
                  `enums.symbolTypes.${changeJsonU[k].oldValue}`
                );
              } else if (this.changeLog.oldValueJson[k]) {
                this.changeLog.oldValueJson[k] = this.checkTranslation(
                  this.changeLog.oldValueJson[k],
                  `enums.symbolTypes.${this.changeLog.oldValueJson[k]}`
                );
              }
              if (changeJsonU[k].newValue) {
                changeJsonU[k].newValue = this.checkTranslation(
                  changeJsonU[k].newValue,
                  `enums.symbolTypes.${changeJsonU[k].newValue}`
                );
              }
            } else if (
              changeJsonU[k].newValue &&
              typeof changeJsonU[k].newValue === 'object'
            ) {
              // Legacy rows can also report no-op "changes" here (reference-equality
              // false positive on the backend), so skip rendering when the
              // two sides are actually equal.
              if (
                this.isDeepEqual(
                  changeJsonU[k].newValue,
                  this.changeLog.oldValueJson[k]
                )
              ) {
                delete changeJsonU[k];
              } else if (k === 'samplingInfo') {
                changeJsonU[k].newValue = this.formatSamplingInfo(
                  changeJsonU[k].newValue
                );
                this.changeLog.oldValueJson[k] = this.formatSamplingInfo(
                  this.changeLog.oldValueJson[k]
                );
              }
            } else {
              if (changeJsonU[k].oldValue) {
                if (
                  typeof changeJsonU[k].oldValue === 'string' &&
                  this.isValidDate(changeJsonU[k].oldValue)
                ) {
                  changeJsonU[k].oldValue = moment(
                    changeJsonU[k].oldValue
                  ).format('DD/MM/YYYY HH:mm:ss');
                }
              } else if (this.changeLog.oldValueJson[k]) {
                if (
                  typeof this.changeLog.oldValueJson[k] === 'string' &&
                  this.isValidDate(this.changeLog.oldValueJson[k])
                ) {
                  this.changeLog.oldValueJson[k] = moment(
                    this.changeLog.oldValueJson[k]
                  ).format('DD/MM/YYYY HH:mm:ss');
                }
              }
              if (changeJsonU[k].newValue) {
                if (
                  typeof changeJsonU[k].newValue === 'string' &&
                  this.isValidDate(changeJsonU[k].newValue)
                ) {
                  changeJsonU[k].newValue = moment(
                    changeJsonU[k].newValue
                  ).format('DD/MM/YYYY HH:mm:ss');
                }
              }
              changeJsonU[k].oldValue = changeJsonU[k].oldValue
                ? changeJsonU[k].oldValue
                : this.changeLog.oldValueJson[k];
            }
          });
        }
        return changeJsonU;
      },
      removedData() {
        const changeJsonR = { ...this.changeLog.changeJson.r } || null;
        return this.prepareData(changeJsonR);
      },
    },
    methods: {
      prepareData(changeLog) {
        const changeLogData = { ...changeLog } || null;
        if (changeLogData) {
          delete changeLogData.documentId;

          const keys = Object.keys(changeLogData) || [];
          if (keys.filter((k) => Number(k)).length > 0) {
            keys.forEach((k) => {
              const arrayKeys = Object.keys(changeLogData[k]);
              arrayKeys.forEach((opKey) => {
                if (opKey === 'laboratory') {
                  changeLogData[k][opKey] = this.checkTranslation(
                    changeLogData[k][opKey],
                    `constants.${changeLogData[k][opKey]}`
                  );
                } else if (
                  typeof changeLogData[k][opKey] === 'string' &&
                  this.isValidDate(changeLogData[k][opKey])
                ) {
                  changeLogData[k][opKey] = moment(
                    changeLogData[k][opKey]
                  ).format('DD/MM/YYYY HH:mm:ss');
                }
              });
            });
            return changeLogData;
          }
          keys.forEach((k) => {
            if (
              typeof changeLogData[k] === 'string' &&
              this.isValidDate(changeLogData[k])
            ) {
              changeLogData[k] = moment(changeLogData[k]).format(
                'DD/MM/YYYY HH:mm:ss'
              );
            } else if (k === 'operationRegistry') {
              const operationRegistryKeys = Object.keys(changeLogData[k]);
              operationRegistryKeys.forEach((opKey) => {
                if (opKey === 'id') {
                  changeLogData.operationRegistryId = changeLogData[k][opKey];
                } else if (opKey === 'symbolType' && changeLogData[k][opKey]) {
                  changeLogData[opKey] = this.checkTranslation(
                    changeLogData[k][opKey],
                    `enums.symbolTypes.${changeLogData[k][opKey]}`
                  );
                } else {
                  changeLogData[opKey] = changeLogData[k][opKey];
                }
              });
              changeLogData.operationRegistry = null;
            } else if (k === 'reason') {
              changeLogData[k] = this.checkTranslation(
                QUARANTINE_REASONS_DESC[changeLogData[k]],
                `enums.quarantineReasons.${
                  QUARANTINE_REASONS_DESC[changeLogData[k]]
                }`
              );
            } else if (k === 'sector') {
              changeLogData[k] = this.checkTranslation(
                changeLogData[k],
                `enums.sectors.${changeLogData[k]}`
              );
            } else if (k === 'sampleDescription') {
              changeLogData[k] = this.checkTranslation(
                changeLogData[k],
                `enums.sampleDescriptions.${changeLogData[k]}`
              );
            } else if (k === 'source') {
              changeLogData[k] =
                changeLogData[k] === 0
                  ? this.$t('quarantine.sourceLabel.quarantine')
                  : this.$t('quarantine.sourceLabel.receivedSamples');
            } else if (k === 'type' || k === 'specificationType') {
              changeLogData[k] = this.parseSpecificationFormat(
                changeLogData[k]
              );
            } else if (k === 'samplingInfo') {
              changeLogData[k] = this.formatSamplingInfo(changeLogData[k]);
            } else if (k === 'laboratory') {
              changeLogData[k] = this.checkTranslation(
                changeLogData[k],
                `constants.${changeLogData[k]}`
              );
            } else if (changeLogData[k] && changeLogData[k].length) {
              changeLogData[k] = changeLogData[k].toString();
            }
          });
          return changeLogData;
        }
        return changeLogData;
      },
      isValidDate(date) {
        if (!date) return false;
        return moment(date, moment.ISO_8601, true).isValid();
      },
      parseSpecificationFormat(value) {
        if (!value) return '';
        switch (value) {
          case 'None':
            return this.$t('specifications.limitFormats.absent');
          case 'FreeText':
            return this.$t('specifications.limitFormats.freeText');
          case 'Selection':
            return this.$t('specifications.limitFormats.selection');
          case 'Range':
            return this.$t('specifications.limitFormats.range');
          default:
            return '';
        }
      },
      isMultiRow(key) {
        if (Number(key) || Number(key) === 0) return true;
        const value =
          this.updatedData[key]?.newValue ??
          this.addedData[key] ??
          this.removedData[key];
        return Boolean(
          value && typeof value === 'object' && !Array.isArray(value)
        );
      },
      visibleNestedKeys(key) {
        return Object.keys(this.updatedData[key].newValue).filter(
          (updatedKey) =>
            this.changeLog.oldValueJson[key][updatedKey] ||
            this.updatedData[key].newValue[updatedKey]
        );
      },
      isDeepEqual(a, b) {
        if (a === b) return true;
        if (
          typeof a !== 'object' ||
          typeof b !== 'object' ||
          a === null ||
          b === null
        ) {
          return false;
        }
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        return aKeys.every((key) => this.isDeepEqual(a[key], b[key]));
      },
      formatSamplingInfo(info) {
        if (!info) return info;
        const formatted = { ...info };
        if (
          typeof formatted.date === 'string' &&
          this.isValidDate(formatted.date)
        ) {
          formatted.date = moment(formatted.date).format('DD/MM/YYYY HH:mm:ss');
        }
        if (formatted.sampledBy) {
          formatted.sampledBy = this.checkTranslation(
            formatted.sampledBy,
            `enums.sampledBy.${formatted.sampledBy}`
          );
        }
        return formatted;
      },
      parsePermissions(value) {
        const currentPermissions = value;
        if (!currentPermissions || !currentPermissions.length) return '';
        return currentPermissions
          .map((p) => this.checkTranslation(p, `changeLog.permissions.${p}`))
          .toString()
          .split(',')
          .join(',\n');
      },
      checkTranslation(value, translation) {
        if (!value) return '';
        if (value === 'analysisId') {
          return this.$t('changeLog.fields.analysisId');
        }
        if (value === 'analysisCode') {
          return this.$t('filters.analysisId');
        }
        if (value === 'analysisDescription') {
          return this.$t('filters.analysisDescriptionFull');
        }
        if (this.$te(translation)) {
          return this.$t(translation);
        }
        return `{${value}}`;
      },
    },
  };
</script>

<style lang="scss">
  .changelog-detail {
    .changelog-detail-content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
    .col-title {
      font-weight: bold;
      color: $--color-white;
      margin-bottom: 0.5rem;
    }
    .updated-col {
      margin-bottom: 0.5rem;
    }
    .multiples-items-row {
      padding: 0.5rem 0;
      &:not(:last-child) {
        border-bottom: 1px solid $--color-white;
      }
    }
    .multiline {
      white-space: pre-wrap;
    }
  }
</style>
